import { useClickRef } from '@make-software/csprclick-ui';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePostAccounts } from '../hooks/event-store-hooks';
import { isValidPublicKey } from '../utils/account';
import { disconnectWallet, setActiveWalletAndKey } from '../redux/wallet';
import { SignResult } from '@make-software/csprclick-core-client';
import { RootState } from 'typesafe-actions';
import { AccountModel } from '@block-explorer/entities';

let csprClickInstance;

const getCsprClickInstance = () => {
  try {
    if (csprClickInstance == null) {
      csprClickInstance = window.csprclick;
    }

    return csprClickInstance;
    // eslint-disable-next-line no-empty
  } catch (err) {}
};

interface CsprClickService {
  // connected: boolean;
  publicKey: string;
  signIn: () => void;
  signOut: () => void;
  walletProvider: () => string | undefined;
  supportTransactions: () => boolean;
  sign: (
    deployJson: string,
    senderPublicKey: string
  ) => Promise<SignResult | undefined>;
  signMessage: (
    message: string,
    senderPublicKey: string
  ) => Promise<SignResult | undefined>;
  buyCSPR: () => void;
}

export const useCsprClickService = (): CsprClickService => {
  const dispatch = useDispatch();
  const [publicKey, setPublicKey] = useState('');
  const clickRef = useClickRef();
  const click = getCsprClickInstance();
  const { activePublicKey: storedPublicKey } = useSelector(
    (state: RootState) => state.wallet
  );

  const { postData: registerAccountHashes } = usePostAccounts();

  const handleConnect = (action) => {
    const publicKeyChanged = action.public_key !== storedPublicKey;

    if (isValidPublicKey(action.public_key)) {
      dispatch(
        setActiveWalletAndKey({
          walletType: action.provider,
          publicKey: action.public_key,
        })
      );

      setPublicKey(action.public_key);

      if (!storedPublicKey || publicKeyChanged) {
        registerAccountHashes(
          {
            account_hashes: [
              AccountModel({
                publicKeyHex: action.public_key,
              }).getAccountHash(),
            ],
          },
          undefined
        );
      }
    }
  };

  const handleDisconnect = () => {
    dispatch(disconnectWallet());
  };

  useEffect(() => {
    if (clickRef === undefined || click === undefined) return;

    if (clickRef.getActiveAccount() === null) {
      handleDisconnect();
    }

    click.on('csprclick:signed_in', async (evt: any) => {
      handleConnect(evt.account);
    });
    click.on('csprclick:switched_account', async (evt: any) => {
      handleConnect(evt.account);
    });
    click.on('csprclick:signed_out', async (evt: any) => {
      handleDisconnect();
    });
    click.on('csprclick:disconnected', async (evt: any) => {
      handleDisconnect();
    });
  }, [clickRef, click]);

  const signIn = () => {
    clickRef?.signIn();
  };

  const signOut = () => {
    clickRef?.signOut();
  };

  const sign = async (
    deployJson: string,
    senderPublicKey: string
  ): Promise<SignResult | undefined> => {
    return clickRef?.sign(deployJson, senderPublicKey);
  };

  const signMessage = async (message: string, senderPublicKey: string) => {
    return clickRef?.signMessage(message, senderPublicKey);
  };

  const walletProvider = () => {
    return clickRef?.provider?.name();
  };
  const supportTransactions = () => {
    const supportedItems: string[] =
      clickRef?.getActiveAccount()?.['providerSupports'] || [];

    return supportedItems.includes('sign-transactionv1');
  };

  const buyCSPR = () => {
    return clickRef?.showByCsprUi();
  };

  return {
    publicKey,
    walletProvider,
    supportTransactions: supportTransactions,
    signIn: signIn,
    signOut: signOut,
    sign: sign,
    signMessage: signMessage,
    buyCSPR,
  };
};
